import { useAppSelector } from '@src/state/hooks';
import { purchasedTier } from '@src/components/PremiumBadge/tierIdentity';
import './PremiumPurchaseSurvey.css';
import { isDotCom } from '@src/site';
import { useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { Choice, ChoicePanel, Textarea, Stack, Typography } from '@src/components';

const reasons = ['themes', 'organizer_tools', 'personal_address', 'no_ads', 'support', 'bundle', 'other'] as const;
const icons = ['🎨', '🛠️', '🔗', '🚫', '❤️', '✨', '💬'];
export interface PurchaseSurveyHandle { flush: () => Promise<void> }
type SurveyProps = { enabled: boolean; userId?: number; ref?: Ref<PurchaseSurveyHandle> };
export function PremiumPurchaseSurvey(props: SurveyProps) {
  const paymentId = useAppSelector(state => state.session.user?.premium?.access.currentPaymentId);
  return <PremiumPurchaseSurveyContent key={`${props.userId}:${paymentId}:${props.enabled}`} {...props} />;
}
function PremiumPurchaseSurveyContent({ enabled, userId, ref }: SurveyProps) {
  const { t } = useTranslation();
  const user = useAppSelector(state => state.session.user);
  const tier = purchasedTier(user);
  const fallbackReasons = reasons.filter(reason =>
    (reason !== 'organizer_tools' || tier !== 'tier_1') &&
    (reason !== 'personal_address' || !tier || tier === 'tier_3')
  );
  const [survey, setSurvey] = useState<{ paymentId: number; allowedReasons: string[]; reason: string; comment: string } | null>(null);
  const [error, setError] = useState(false);
  const queue = useRef(Promise.resolve());
  const comment = useRef('');
  const lastQueuedComment = useRef('');
  useEffect(() => {
    if (!enabled || !userId) return;
    const controller = new AbortController();
    void fetch('/api/premium/purchase-survey', { credentials: 'same-origin', cache: 'no-store', signal: controller.signal })
      .then(async response => {
        if (!response.ok) return;
        const { survey: data } = await response.json();
        if (!controller.signal.aborted && data) {
          comment.current = lastQueuedComment.current = data.comment || '';
          setSurvey({ ...data, reason: data.reason || '', comment: data.comment || '' });
        }
      }).catch(() => {});
    return () => controller.abort();
  }, [enabled, userId]);
  function enqueue(payload: object) {
    if (!survey) return;
    const body = JSON.stringify({ paymentId: survey.paymentId, ...payload });
    // Serialize writes so rapid changes retain click order. A retry uses the same
    // event ID, allowing the server to discard an already-committed selection.
    queue.current = queue.current.then(async () => {
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const response = await fetch('/api/premium/purchase-survey', {
            method: 'POST', credentials: 'same-origin', keepalive: true,
            headers: { 'Content-Type': 'application/json' }, body,
            signal: AbortSignal.timeout(2500),
          });
          if (!response.ok) throw new Error('Survey save failed');
          setError(false); return;
        } catch { if (attempt === 1) setError(true); }
      }
    });
  }
  function saveComment() {
    if (comment.current === lastQueuedComment.current) return;
    lastQueuedComment.current = comment.current;
    enqueue({ action: 'comment', surveyComment: comment.current });
  }
  useImperativeHandle(ref, () => ({ flush: async () => {
    saveComment();
    if (survey && (survey.reason || comment.current.trim())) enqueue({ action: 'complete' });
    // Feedback is optional: slow networking must not trap the user here.
    await Promise.race([queue.current, new Promise<void>(resolve => setTimeout(resolve, 1200))]);
  } }));
  if (!enabled || !survey) return null;
  return <Stack gap={3} className="purchase-survey" testId="purchase-survey">
    <div className="purchase-survey__welcome">
      <Typography as="h3" role="title/card"><span className="purchase-survey__heart" aria-hidden="true">♡</span> {t('purchaseSurvey.thanks')}</Typography>
      <Typography as="p" role="body/base">{t('purchaseSurvey.welcome')}</Typography>
    </div>
    <Typography as="h3" role="title/card" className="purchase-survey__question">{t('purchaseSurvey.title')}</Typography>
    <ChoicePanel plain description={t('purchaseSurvey.subtitle')}>
      {reasons.map((value, index) => !(survey.allowedReasons ?? fallbackReasons).includes(value) || (value === 'no_ads' && isDotCom()) ? null : <Choice key={value} name="purchase-reason" value={value}
        title={t(`purchaseSurvey.reasons.${value}`)} description={t(`purchaseSurvey.hints.${value}`)} icon={<span>{icons[index]}</span>}
        checked={survey.reason === value} onCheckedChange={() => {
          setSurvey({ ...survey, reason: value });
          enqueue({ action: 'reason', reason: value, eventId: crypto.randomUUID() });
        }} testId={`purchase-survey-${value}`} />)}
    </ChoicePanel>
    <Textarea label={t('purchaseSurvey.comment')} value={survey.comment} onValueChange={value => {
      comment.current = value; setSurvey({ ...survey, comment: value });
    }} onBlur={saveComment} maxLength={2000} showCount rows={3} testId="purchase-survey-comment" />
    {error && <p role="status">{t('purchaseSurvey.error')}</p>}
  </Stack>;
}
